/**
 * TelegramConnectPage — any logged-in user can link their Telegram account
 * via deep-link or QR code, toggle notifications, and send a test message.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  TelegramLogo, CheckCircle, XCircle, ArrowSquareOut, BellRinging, BellSlash,
  Plugs, ArrowClockwise, Copy,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function TelegramConnectPage() {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/telegram/me');
      setStatus(r.data);
    } catch { setStatus({ linked: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateLink = async () => {
    setBusy(true);
    try {
      const r = await api.post('/telegram/connect-link');
      setLink(r.data.deep_link);
      window.open(r.data.deep_link, '_blank', 'noopener');
      // Poll for connection
      const start = Date.now();
      const poll = setInterval(async () => {
        try {
          const r2 = await api.get('/telegram/me');
          if (r2.data?.linked) {
            setStatus(r2.data);
            clearInterval(poll);
            toast.success('🎉 Telegram connected!');
          } else if (Date.now() - start > 180000) {  // 3 min
            clearInterval(poll);
          }
        } catch { /* noop */ }
      }, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate link');
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    try {
      const r = await api.post('/telegram/test');
      if (r.data?.ok) toast.success('✅ Test sent — Telegram check karo');
      else toast.error(r.data?.error || 'Test failed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const toggleNotifs = async (enabled) => {
    try {
      await api.patch('/telegram/notifications', { enabled });
      await load();
      toast.success(enabled ? '🔔 Notifications ON' : '🔕 Notifications OFF');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Telegram unlink karna hai? Aapko notifications nahi milengi.')) return;
    try {
      await api.post('/telegram/disconnect');
      await load();
      setLink(null);
      toast.success('Telegram disconnected');
    } catch (err) { toast.error('Failed'); }
  };

  if (!status) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="telegram-page">
      <div className="flex items-center gap-3">
        <TelegramLogo size={36} weight="duotone" className="text-sky-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Telegram Notifications</h1>
          <p className="text-xs text-gray-500">Booking file uploads, alerts, aur important updates Telegram pe paao</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="ml-auto"><ArrowClockwise size={14} /></Button>
      </div>

      {!status.linked ? (
        <div className="bg-white border rounded-xl p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
            <b>Setup (1 min):</b>
            <ol className="list-decimal pl-5 mt-1 space-y-0.5">
              <li>Neeche "Connect Telegram" button click karo</li>
              <li>Telegram app khulega @{status.bot_username} bot ke saath</li>
              <li>Bot mein "START" button press karo</li>
              <li>Bas! Yeh page automatically ✅ ho jayega</li>
            </ol>
          </div>
          <Button
            onClick={generateLink}
            disabled={busy}
            className="w-full bg-sky-500 hover:bg-sky-600 gap-2 h-12 text-base"
            data-testid="connect-btn"
          >
            <TelegramLogo size={20} weight="bold" />
            {busy ? 'Generating…' : 'Connect Telegram'}
          </Button>
          {link && (
            <div className="text-xs text-gray-600 space-y-1">
              <div>Telegram app nahi khula? Yeh link manually open karo:</div>
              <div className="flex gap-1">
                <code className="flex-1 bg-gray-50 p-2 rounded font-mono text-[11px] truncate">{link}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success('Copied'); }}><Copy size={12} /></Button>
                <a href={link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ArrowSquareOut size={12} /></Button></a>
              </div>
              <div className="text-amber-700">Link 30 min me expire ho jayega.</div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="connected-banner">
            <CheckCircle size={28} weight="duotone" className="text-emerald-600" />
            <div>
              <div className="font-bold text-emerald-900">Telegram Connected ✅</div>
              {status.username && <div className="text-xs text-emerald-700">@{status.username}</div>}
              {status.linked_at && <div className="text-[10px] text-emerald-600">Linked {new Date(status.linked_at).toLocaleString('en-IN')}</div>}
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {status.notifications_enabled ? <BellRinging size={18} className="text-emerald-600" /> : <BellSlash size={18} className="text-gray-400" />}
                  Notifications
                </div>
                <div className="text-xs text-gray-500">
                  {status.notifications_enabled ? 'Aapko bookings file aur alerts milenge' : 'Notifications band hain'}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => toggleNotifs(!status.notifications_enabled)}
                className={status.notifications_enabled ? 'text-emerald-700' : 'text-gray-700'}
                data-testid="toggle-notifs-btn"
              >
                {status.notifications_enabled ? 'Turn OFF' : 'Turn ON'}
              </Button>
            </div>
            <div className="border-t pt-3 flex gap-2">
              <Button size="sm" variant="outline" className="gap-1" onClick={sendTest} data-testid="test-btn">
                <BellRinging size={14} /> Send Test Message
              </Button>
              <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200 hover:bg-red-50 ml-auto" onClick={disconnect} data-testid="disconnect-btn">
                <Plugs size={14} /> Disconnect
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
            💡 <b>Aap kab notifications paoge:</b>
            <ul className="list-disc pl-5 mt-1">
              <li>Koi agency naya booking file upload kare (aap admin/owner ho ya file aapko assigned hai)</li>
              <li>Future me: bank statement uploads, vendor fund requests, etc.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
